import {
  styled,
  Table,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Tooltip,
  TableContainer,
  Paper,
  // TextField,
  // InputAdornment,
} from "@mui/material";
// import SearchIcon from "@mui/icons-material/Search";
// import { useMutation } from "react-query";
// import { getAllHIstory } from "../../EMR/services";
import { useMemo } from "react";
import moment from "moment";
import CustomTableBody from "../../utilis/component/CustomTableBody";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 10,
    fontWeight: 700,
    padding: "6px 10px",
    position: 'sticky',
    top: 0,
    zIndex: 10,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderRight: "1px solid rgba(255, 255, 255, 0.2)",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px 10px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderRight: "1px solid #eef0f2",
    lineHeight: 1.6,
  },
}));

const titles = {
  all:"all",
  Vitals: "vitals",
  Allergy: "allergy",
  Complaints: "complaints",
  "Medical History": "medicalHistory",
  "Social History": "socialHistory",
  "Family History": "familyHistory",
  "Physical Examination": "physicalExamination",
  lab: "lab",
  Radiology: "radiology",
  Diagnosis: "diagnosis",
  Problems: "problems",
  Procedures: "procedures",
  Prescription: "prescription",
  "Management Plan": "managementPlan",
  "Treatment Plan": "treatmentPlan",
  "clinical Impression": "clinicalImpression",
  "Upload Document": "uploadDocument",
  "Dental Procedure": "dentalProcedure",
};

const PATIENT_LEVEL_SECTIONS = new Set(["Allergy", "Social History"]);

function normalizeAllergyHistory(rawList: any[]): any[] {
  if (!Array.isArray(rawList) || !rawList.length) return [];
  if (rawList[0]?.data) return rawList;
  return [{ encounterId: null, data: rawList }];
}

function normalizeSocialHistoryHistory(rawList: any[]): any[] {
  if (!Array.isArray(rawList) || !rawList.length) return [];
  if (rawList[0]?.data) return rawList;
  return [{ encounterId: null, data: rawList }];
}

const FIELD_FALLBACKS: Partial<
  Record<keyof typeof titles, Record<string, string[]>>
> = {
  Allergy: {
    "allergyType.allergyTypeName": ["allergyType"],
    "allergen.allergenName": ["allergenDescription", "description"],
    "severity.allergySeverityName": ["allergySeverity"],
    "lifeCycleStatus.description": ["status"],
  },
  "Social History": {
    "description.snomedDescription": ["description"],
    "category.name": ["name"],
    "quantity.quantityDesc": ["quantity"],
  },
};

/** ROS / physical exam history often arrives as a bucket object keyed by body system (see EMR PhysicalExaminationRos), not as a flat array. */
function coercePhysicalExaminationToEntries(raw: any): any[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return [raw];
  return [];
}

function expandPhysicalExaminationRosNode(node: any, encounterId?: any): any[] {
  if (!node || typeof node !== "object") return [];

  const metaKeys = new Set([
    "encounterId",
    "encounter",
    "id",
    "createdDate",
    "createdByDate",
    "patientId",
    "visitId",
  ]);

  const rows: any[] = [];
  for (const [systemKey, payload] of Object.entries(node)) {
    if (metaKeys.has(systemKey)) continue;
    const p = payload as any;
    if (p && typeof p === "object" && Array.isArray(p.ros)) {
      const remarks = p.remarks ?? p.remark ?? "";
      const status = p.severity ?? p.status ?? "";
      for (const rosItem of p.ros) {
        rows.push({
          encounterId,
          bodySystemName: systemKey,
          rosName: rosItem?.rosName ?? rosItem?.ros_name ?? "",
          rosStatus: status,
          rosRemarks: remarks,
        });
      }
    }
  }
  if (rows.length) return rows;

  const rosName = node.rosName ?? node.ros_name;
  const bodySystem =
    node.bodySystemName ??
    node.body_system_name ??
    node.bodySystem?.bodySystemName;
  if (rosName != null || bodySystem != null) {
    return [
      {
        ...node,
        encounterId: encounterId ?? node.encounterId ?? node.encounter,
        bodySystemName: bodySystem ?? "",
        rosName: rosName ?? "",
        rosStatus:
          node.rosStatus ??
          node.severity ??
          node.status ??
          node.ros_status ??
          "",
        rosRemarks:
          node.rosRemarks ?? node.remarks ?? node.remark ?? node.ros_remarks ?? "",
      },
    ];
  }

  return [];
}

function normalizePhysicalExaminationHistory(raw: any): any[] {
  return coercePhysicalExaminationToEntries(raw).flatMap((item: any) => {
    if (!item || typeof item !== "object") return [];

    const encounterId = item.encounterId ?? item.encounter;

    if (Array.isArray(item.data)) {
      // Some APIs return a simple "physicalExamination" shape:
      // [{ encounterId, data: [{ description, createdDate, ... }] }]
      // In that case, keep the rows as-is (attach encounterId for filtering/grouping).
      if (item.data.some((row: any) => row && typeof row === "object" && "description" in row)) {
        return item.data.map((row: any) => ({
          ...row,
          encounterId: row?.encounterId ?? encounterId,
        }));
      }

      // ROS bucket style -> expand to rows and create a user-friendly description.
      return item.data.flatMap((row: any) =>
        expandPhysicalExaminationRosNode(row, encounterId).map((r: any) => ({
          ...r,
          description: [
            r.bodySystemName ? `${r.bodySystemName}` : "",
            r.rosName ? `${r.rosName}` : "",
            r.rosStatus ? `(${r.rosStatus})` : "",
            r.rosRemarks ? `- ${r.rosRemarks}` : "",
          ]
            .filter(Boolean)
            .join(" "),
        })),
      );
    }

    if (
      item.data &&
      typeof item.data === "object" &&
      !Array.isArray(item.data)
    ) {
      return expandPhysicalExaminationRosNode(item.data, encounterId);
    }

    return expandPhysicalExaminationRosNode(item, encounterId);
  });
}

interface Props {
  title: keyof typeof titles;
  tableprops: {
    heading: string;
    key: string;
    date?: string;
    format?: string;
    numberType?: string;
  }[];
  maxHeight?: string | number;
  data?: any;
  isLoading?: boolean;
  filterDate?: string;
  filterEncounterId?: number | string | null;
  globalSearch?: string;
  patientLevel?: boolean;
}

const GenericHistoryTable = ({
  title,
  tableprops,
  maxHeight = 300,
  data,
  isLoading,
  filterDate,
  filterEncounterId,
  globalSearch,
  patientLevel = false,
}: Props) => {
  const renderdata = useMemo(() => {
    const isPatientLevel =
      patientLevel || PATIENT_LEVEL_SECTIONS.has(title as string);
    const activeEncounterFilter = isPatientLevel ? null : filterEncounterId;
    const activeDateFilter = isPatientLevel ? "" : filterDate;

    const source = data?.response ?? data;
    let rawList =
      source?.[titles[title]] ??
      (title === "Allergy" ? source?.allergies : undefined) ??
      (title === "Social History"
        ? source?.socialhistory ?? source?.social_history
        : undefined);

    if (title === "Allergy") {
      rawList = normalizeAllergyHistory(rawList);
    } else if (title === "Social History") {
      rawList = normalizeSocialHistoryHistory(rawList);
    } else if (title === "Physical Examination") {
      const looksGrouped =
        Array.isArray(rawList) &&
        rawList.length > 0 &&
        rawList[0] &&
        typeof rawList[0] === "object" &&
        "data" in rawList[0] &&
        Array.isArray((rawList[0] as any).data);
      rawList = looksGrouped ? rawList : normalizePhysicalExaminationHistory(rawList);
    }

    const list = Array.isArray(rawList) ? rawList : [];
    if (!list.length) return [];

    const lowerSearch = globalSearch?.toLowerCase() || "";

    const processItem = (item: any) => {
      if (activeEncounterFilter != null && activeEncounterFilter !== "") {
        const itemEncounter =
          item.encounterId ??
          item.encounter ??
          item.procedure?.encounterId ??
          item.procedure?.encounter ??
          null;
        if (
          itemEncounter != null &&
          String(itemEncounter) !== String(activeEncounterFilter)
        ) {
          return false;
        }
      }

      if (
        activeDateFilter &&
        (activeEncounterFilter == null || activeEncounterFilter === "")
      ) {
        const dateVal =
          item.createdDate ||
          item.createdByDate ||
          item.procedure?.createdDate ||
          item.onsetDate ||
          item.fromDate;
        if (dateVal) {
          const mDate = moment(dateVal);
          if (!mDate.isSame(moment(activeDateFilter), "day")) return false;
        } else if (!isPatientLevel) {
          return false;
        }
      }

      if (lowerSearch) {
        const matches = tableprops.some((prop) => {
          const val = resolveValue(getNestedValue(item, prop.key, title));
          return val.toString().toLowerCase().includes(lowerSearch);
        });
        if (!matches) return false;
      }

      return true;
    };

    if (list[0]?.data) {
      const grouped = isPatientLevel
        ? list
        : list.filter((group: any) => {
            if (activeEncounterFilter == null || activeEncounterFilter === "") {
              return true;
            }
            const groupEncounterId = group?.encounterId ?? group?.encounter;
            if (groupEncounterId == null) return true;
            return String(groupEncounterId) === String(activeEncounterFilter);
          });

      const normalizedGroups = isPatientLevel
        ? [
            {
              encounterId: null,
              data: grouped.flatMap((group: any) =>
                Array.isArray(group?.data) ? group.data : [],
              ),
            },
          ]
        : grouped;

      return normalizedGroups
        .map((group: any) => ({
          ...group,
          data: (group.data || []).filter(processItem),
        }))
        .filter((group: any) => group.data.length > 0);
    }

    const filteredFlat = list.filter(processItem);

    return Object.values(
      filteredFlat.reduce((acc: any, item: any) => {
        const id = item.encounter || item.encounterId || "all";
        if (!acc[id]) {
          acc[id] = { encounterId: id, data: [] };
        }
        acc[id].data.push(item);
        return acc;
      }, {}),
    );
  }, [
    data,
    title,
    filterDate,
    filterEncounterId,
    globalSearch,
    tableprops,
    patientLevel,
  ]);

  function getNestedValue(
    obj: any,
    key: string,
    sectionTitle: keyof typeof titles = title,
  ) {
    const primary = readNestedValue(obj, key);
    if (primary != null && primary !== "") return primary;

    const fallbacks = FIELD_FALLBACKS[sectionTitle]?.[key] || [];
    for (const fallbackKey of fallbacks) {
      const fallbackValue = readNestedValue(obj, fallbackKey);
      if (fallbackValue != null && fallbackValue !== "") {
        return fallbackValue;
      }
    }

    return primary;
  }

  function readNestedValue(obj: any, key: string) {
    const splited = key.split(".");
    if (splited.length > 1) {
      return splited.reduce((acc: any, curr: string) => {
        if (acc && typeof acc === "object" && curr in acc) {
          return acc[curr];
        }
        return undefined;
      }, obj);
    }
    return obj?.[key];
  }

  function resolveValue(value: any) {
    if (value == null) return "";

    if (typeof value !== "object")
      return String(value === 0 ? "0" : value || "").replace(/#\$/g, " ");

    if ("value" in value) return value.value;

    if ("name" in value) return value.name;
    if ("snomedDescription" in value) return value.snomedDescription;
    if ("quantityDesc" in value) return value.quantityDesc;
  return JSON.stringify(value);
  }

  const renderCellValue = (row: any, tableProp: any) => {
    let value = getNestedValue(row, tableProp.key);
    if (value || value === 0) {
      if (tableProp?.date) {
        let m = tableProp?.format
          ? moment(value, tableProp.format, true)
          : moment.invalid();
        if (!m.isValid()) {
          m = moment(value);
        }
        return m.isValid() ? m.format("DD-MM-YYYY") : String(value ?? "");
      }
      if (Boolean(tableProp.numberType)) {
        return tableProp.numberType === "round"
          ? Number(value).toFixed(2)
          : `${value}`;
      }
      
      if (typeof value === "number") {
        return `${value}`;
      }

      const displayValue = resolveValue(value);

      if (tableProp.key === "notes" || tableProp.key === "medication") {
        return (
          <Tooltip title={displayValue || ""} arrow placement="right">
            <span
              style={{
                display: "inline-block",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              {displayValue}
            </span>
          </Tooltip>
        );
      }

      return displayValue;
    }
    return "";
  };

  if (isLoading) return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: maxHeight, borderRadius: 2, border: "1px solid #eef0f2" }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ width: "100%", position: 'relative' }}>
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ 
          maxHeight: maxHeight, 
          border: "1px solid #eef0f2", 
          borderRadius: "8px", 
          overflow: "auto",
          '&::-webkit-scrollbar': { width: '6px', height: '6px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
          '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#999' },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: 60 }}>SL NO</StyledTableCell>
              {tableprops.map((prop, i) => (
                <StyledTableCell key={i}>{prop.heading}</StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <CustomTableBody
            alert={"No data found"}
            dataLoading={Boolean(isLoading)}
            isData={Boolean(renderdata.length > 0)}
            totalSpan={{
              col: tableprops.length + 1,
              row: 5,
            }}
          >
            {renderdata.map((item: any) => (
              item?.data?.map((row: any, j: number) => (
                <TableRow key={`${j}`} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 500, borderLeft: "1px solid #eef0f2" }}>{j + 1}</TableCell>
                  {tableprops.map((prop, k) => (
                    <TableCell key={k} sx={{ borderLeft: "1px solid #eef0f2" }}>
                      {prop.key !== "" && renderCellValue(row, prop)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ))}
          </CustomTableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GenericHistoryTable;
